import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import './i18n';
import { supabase } from './lib/supabaseClient';
import {
  fetchProfile,
  signInWithPassword,
  signUpWithPassword,
  signInWithGoogle,
  signOut,
} from './lib/auth';
import {
  listContent,
  upsertContent,
  deleteContent,
  listAllPrayers,
  submitPrayer,
  approvePrayer,
  deletePrayer,
  togglePrayerLike,
  listMyPrayerLikes,
  getSettings,
  updateSettings,
} from './lib/data';

/* ══ 7 Themes (dunkle Anthrazit-Basis, Goldakzente variieren) ══ */
const THEMES = [
  {
    id: 't0',
    name: 'Gold & Anthrazit',
    desc: 'Original – warmes Glaubens-Gold auf tiefem Schiefer',
    dark: '#1E232A',
    gray: '#2A303C',
    gold: '#D49D2A',
    goldH: '#B8821F',
    bg: '#F3F4F6',
    white: '#FFFFFF',
    wall: '#0F1318',
    text: '#111827',
    muted: '#6B7280',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
  {
    id: 't1',
    name: 'Marine & Türkis',
    desc: 'Tiefes Marineblau mit lebendigem Türkis',
    dark: '#0F1B29',
    gray: '#1A2B3C',
    gold: '#1DA89E',
    goldH: '#158079',
    bg: '#EFF6FA',
    white: '#FFFFFF',
    wall: '#060E17',
    text: '#0C1A27',
    muted: '#4A7A8A',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
  {
    id: 't2',
    name: 'Nacht & Purpur',
    desc: 'Nachtblaues Indigo mit sattem Purpur',
    dark: '#12102A',
    gray: '#1C1B3C',
    gold: '#9B59B6',
    goldH: '#7D3C98',
    bg: '#F5F0FA',
    white: '#FFFFFF',
    wall: '#080614',
    text: '#180F2E',
    muted: '#6B5A8A',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
  {
    id: 't3',
    name: 'Schiefer & Jade',
    desc: 'Warmes Dunkelgrau mit frischem Jadegrün',
    dark: '#1B2220',
    gray: '#273028',
    gold: '#27AE60',
    goldH: '#1E8449',
    bg: '#EFF5F1',
    white: '#FFFFFF',
    wall: '#09100C',
    text: '#0F1A12',
    muted: '#4A6A55',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
  {
    id: 't4',
    name: 'Obsidian & Kupfer',
    desc: 'Tiefes Schwarz mit warmem Kupfer-Akzent',
    dark: '#18120E',
    gray: '#251C15',
    gold: '#CA6C2A',
    goldH: '#A3551E',
    bg: '#FAF5F0',
    white: '#FFFFFF',
    wall: '#0D0905',
    text: '#1C1008',
    muted: '#7A5A45',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
  {
    id: 't5',
    name: 'Stahl & Azur',
    desc: 'Kühles Stahlblau mit brillantem Azur',
    dark: '#0D1B2A',
    gray: '#162738',
    gold: '#2980B9',
    goldH: '#1F6491',
    bg: '#EFF4FB',
    white: '#FFFFFF',
    wall: '#060D18',
    text: '#0A1622',
    muted: '#456A85',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
  {
    id: 't6',
    name: 'Graphit & Roségold',
    desc: 'Elegantes Graphit mit zartem Roségold',
    dark: '#1F1B1D',
    gray: '#2D272B',
    gold: '#C07A84',
    goldH: '#A0606A',
    bg: '#FAF0F2',
    white: '#FFFFFF',
    wall: '#11090C',
    text: '#1A1215',
    muted: '#7A5A62',
    red: '#EF4444',
    green: '#10B981',
    blue: '#3B82F6',
  },
];

const Ctx = createContext(THEMES[0]);
const useT = () => useContext(Ctx);
const uid = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const toDay = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(
    d.getMonth() + 1
  ).padStart(2, '0')}.${d.getFullYear()}`;
};

/* ══ Seed data ══════════════════════════════════════════════════
   Content (locations/events/news) and prayers now live in Supabase
   (see supabase/migrations/0001_init.sql for seed rows). This local
   copy has been removed — data is fetched at runtime via src/lib/data.js. */

const CAT_COLORS = {
  work: '#F59E0B',
  health: '#EF4444',
  family: '#3B82F6',
  gratitude: '#10B981',
};

function useWindowSize() {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
}

/* ══ Scroll Reveal ("fly-in on scroll") ══════════════════════════
   Lightweight, dependency-free re-implementation of the classic
   "scroll reveal" / "Animate On Scroll (AOS)" pattern used on most
   modern marketing sites: elements start slightly offset + transparent
   and animate to their resting position the first time they enter the
   viewport, using the native IntersectionObserver API.

   Fully configurable per instance via props:
     - direction: 'up' | 'down' | 'left' | 'right' | 'zoom' | 'none'
     - distance:  px offset to travel in from (default 32)
     - duration:  animation length in ms (default 700)
     - delay:     ms to wait before starting (default 0) — use to stagger
                  items inside a grid, e.g. delay={i * 90}
     - threshold: fraction of the element visible before it triggers (0-1)
     - once:      if false, the element re-animates every time it re-enters
                  the viewport (default true = only animate once)
   Automatically disabled for users with prefers-reduced-motion set. */
function useInView({ threshold = 0.15, once = true, rootMargin = '0px 0px -8% 0px' } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, once, rootMargin]);

  return [ref, inView];
}

const REVEAL_OFFSETS = {
  up: (d) => `translateY(${d}px)`,
  down: (d) => `translateY(-${d}px)`,
  left: (d) => `translateX(${d}px)`,
  right: (d) => `translateX(-${d}px)`,
  zoom: () => 'scale(.92)',
  none: () => 'none',
};

function Reveal({
  as: Tag = 'div',
  direction = 'up',
  distance = 32,
  duration = 700,
  delay = 0,
  threshold = 0.15,
  once = true,
  style = {},
  children,
  ...rest
}) {
  const [ref, inView] = useInView({ threshold, once });
  const offset = (REVEAL_OFFSETS[direction] || REVEAL_OFFSETS.up)(distance);

  return (
    <Tag
      ref={ref}
      style={{
        ...style,
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : offset,
        transition: `opacity ${duration}ms cubic-bezier(.16,.8,.3,1) ${delay}ms, transform ${duration}ms cubic-bezier(.16,.8,.3,1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ══ Reusable UI Elements ══ */
function Btn({ children, onClick, v = 'gold', sm, full, disabled, sx = {} }) {
  const C = useT();
  const s = {
    gold: { background: C.gold, color: C.dark, border: 'none' },
    dark: { background: C.dark, color: '#fff', border: 'none' },
    ghost: {
      background: 'transparent',
      color: '#fff',
      border: `1.5px solid ${C.gold}50`,
    },
    outline: {
      background: 'transparent',
      color: C.gold,
      border: `1.5px solid ${C.gold}`,
    },
    danger: { background: C.red, color: '#fff', border: 'none' },
    success: { background: C.green, color: '#fff', border: 'none' },
    muted: {
      background: C.bg,
      color: C.muted,
      border: `1px solid rgba(0,0,0,.15)`,
    },
  };

  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '.4rem',
        fontFamily: 'inherit',
        fontWeight: 700,
        fontSize: sm ? '.75rem' : '.875rem',
        padding: sm ? '.4rem 1rem' : '.75rem 1.5rem',
        borderRadius: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        width: full ? '100%' : 'auto',
        transition: 'all .18s ease-in-out',
        boxShadow: v === 'gold' ? '0 4px 12px rgba(0,0,0,.1)' : 'none',
        ...s[v],
        ...sx,
      }}
    >
      {children}
    </button>
  );
}

const SectionHeader = ({ eyebrow, title, sub, light }) => {
  const C = useT();
  return (
    <div
      style={{
        textAlign: 'center',
        maxWidth: 680,
        margin: '0 auto 3rem',
        padding: '0 1rem',
      }}
    >
      <span
        style={{
          fontSize: '.68rem',
          textTransform: 'uppercase',
          letterSpacing: '.18em',
          fontWeight: 700,
          color: light ? C.gold : C.muted,
          display: 'block',
          marginBottom: '.5rem',
        }}
      >
        {eyebrow}
      </span>
      <h2
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 800,
          fontSize: 'clamp(1.8rem, 4.5vw, 2.5rem)',
          color: light ? '#fff' : C.dark,
          marginBottom: '.75rem',
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
      <div
        style={{
          width: 64,
          height: 4,
          background: C.gold,
          margin: '0 auto',
          borderRadius: 9,
          marginBottom: sub ? 12 : 0,
        }}
      />
      {sub && (
        <p
          style={{
            color: light ? 'rgba(255,255,255,.6)' : C.muted,
            fontSize: '.92rem',
            marginTop: '.75rem',
            lineHeight: 1.6,
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
};

const FieldInput = ({ label, ...p }) => {
  const C = useT();
  return (
    <label
      style={{
        display: 'block',
        fontSize: '.72rem',
        fontWeight: 600,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: C.muted,
        marginBottom: '.85rem',
      }}
    >
      {label}
      <input
        {...p}
        style={{
          display: 'block',
          width: '100%',
          marginTop: '.3rem',
          padding: '.7rem .9rem',
          borderRadius: 10,
          border: `1.5px solid rgba(0,0,0,.12)`,
          fontFamily: 'inherit',
          fontSize: '.875rem',
          background: C.white,
          color: C.text,
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'all 0.2s',
          ...p.style,
        }}
      />
    </label>
  );
};

const FieldSelect = ({ label, children, ...p }) => {
  const C = useT();
  return (
    <label
      style={{
        display: 'block',
        fontSize: '.72rem',
        fontWeight: 600,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: C.muted,
        marginBottom: '.85rem',
      }}
    >
      {label}
      <select
        {...p}
        style={{
          display: 'block',
          width: '100%',
          marginTop: '.3rem',
          padding: '.7rem .9rem',
          borderRadius: 10,
          border: `1.5px solid rgba(0,0,0,.12)`,
          fontFamily: 'inherit',
          fontSize: '.875rem',
          background: C.white,
          color: C.text,
          outline: 'none',
          boxSizing: 'border-box',
          ...p.style,
        }}
      >
        {children}
      </select>
    </label>
  );
};

const FieldTA = ({ label, ...p }) => {
  const C = useT();
  return (
    <label
      style={{
        display: 'block',
        fontSize: '.72rem',
        fontWeight: 600,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: C.muted,
        marginBottom: '.85rem',
      }}
    >
      {label}
      <textarea
        {...p}
        style={{
          display: 'block',
          width: '100%',
          marginTop: '.3rem',
          padding: '.7rem .9rem',
          borderRadius: 10,
          border: `1.5px solid rgba(0,0,0,.12)`,
          fontFamily: 'inherit',
          fontSize: '.875rem',
          background: C.white,
          color: C.text,
          outline: 'none',
          resize: 'vertical',
          boxSizing: 'border-box',
          ...p.style,
        }}
      />
    </label>
  );
};

/* ══ Legal Bar ══════════════════════════════════════════════════ */
function LegalBar() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: '#FFFBEB',
        borderBottom: '1px solid #FDE68A',
        padding: '.5rem 1rem',
        textAlign: 'center',
        fontSize: '.75rem',
        color: '#92400E',
        fontWeight: 500,
      }}
    >
      <span
        style={{
          background: '#FDE68A',
          color: '#92400E',
          padding: '.15rem .5rem',
          borderRadius: 4,
          fontSize: '.65rem',
          fontWeight: 700,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          marginRight: '.5rem',
        }}
      >
        {t('legalBar.badge')}
      </span>
      {t('legalBar.before')} <strong>{t('legalBar.strong')}</strong>{' '}
      {t('legalBar.after')}
    </div>
  );
}

/* ══ Language Switcher ══ */
function LanguageSwitcher({ compact }) {
  const { i18n } = useTranslation();
  const langs = ['de', 'en'];
  const setLang = (l) => i18n.changeLanguage(l);

  return (
    <div
      style={{
        display: 'flex',
        gap: '.25rem',
        background: 'rgba(255,255,255,.06)',
        borderRadius: 8,
        padding: '.2rem',
      }}
    >
      {langs.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          aria-label={l === 'de' ? 'Deutsch' : 'English'}
          style={{
            background:
              i18n.resolvedLanguage === l ? 'rgba(212,157,42,.9)' : 'none',
            border: 'none',
            cursor: 'pointer',
            color: i18n.resolvedLanguage === l ? '#1E232A' : 'rgba(255,255,255,.7)',
            fontSize: '.72rem',
            fontWeight: 800,
            fontFamily: 'inherit',
            padding: compact ? '.3rem .5rem' : '.35rem .65rem',
            borderRadius: 6,
            transition: 'all .15s',
          }}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function Nav({ page, go, auth, onAuth, onLogout }) {
  const C = useT();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width < 992;

  const links = [
    ['home', t('nav.home'), ''],
    ['gebete', t('nav.prayerWall'), ''],
    ['ueber-uns', t('nav.about'), ''],
    ['faq', t('nav.faq'), ''],
    ...(auth.role === 'admin' ? [['admin', t('nav.admin'), '']] : []),
  ];

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: C.dark,
        borderBottom: `4px solid ${C.gold}`,
        boxShadow: '0 4px 16px rgba(0,0,0,.3)',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 1.5rem',
          height: 76,
          display: 'flex',
          alignItems: 'center',
          justifyTarget: 'space-between',
          justifyContent: 'space-between',
        }}
      >
        {/* Brand logo */}
        <button
          onClick={() => {
            go('home');
            setMobileMenuOpen(false);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '.75rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              background: C.gold,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.3rem',
              color: C.dark,
            }}
          >
            <i className="fa-solid fa-cross"></i>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div
              style={{
                fontSize: '.58rem',
                textTransform: 'uppercase',
                letterSpacing: '.18em',
                fontWeight: 700,
                color: C.gold,
                lineHeight: 1,
              }}
            >
              {t('nav.brandEyebrow')}
            </div>
            <div
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 800,
                fontSize: '1.1rem',
                color: '#fff',
                lineHeight: 1.2,
              }}
            >
              {t('nav.brandPrefix')} <span style={{ color: C.gold }}>{t('nav.brandSuffix')}</span>
            </div>
          </div>
        </button>

        {/* Desktop Links */}
        {!isMobile && (
          <nav style={{ display: 'flex', gap: '.25rem', alignItems: 'center' }}>
            {links.map(([p, l, icon]) => (
              <button
                key={p}
                onClick={() => {
                  go(p);
                }}
                style={{
                  background: page === p ? `${C.gold}22` : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: page === p ? C.gold : 'rgba(255,255,255,.75)',
                  fontSize: '.82rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  padding: '.5rem 1rem',
                  borderRadius: 8,
                  transition: 'all .18s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                <i
                  className={`fa-solid ${icon}`}
                  style={{ fontSize: '0.85rem', opacity: 0.8 }}
                />
                {l}
              </button>
            ))}
          </nav>
        )}

        {/* Auth / Menu buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          {!isMobile && <LanguageSwitcher />}
          {!isMobile &&
            (auth.isLoggedIn ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <span
                  style={{
                    fontSize: '.75rem',
                    color: 'rgba(255,255,255,.5)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  <i className="fa-solid fa-user-circle" />{' '}
                  {auth.role === 'admin' ? '' : ''}
                  {auth.user?.split('@')[0]}
                </span>
                <Btn v="ghost" sm onClick={onLogout}>
                  {t('nav.logout')}
                </Btn>
              </div>
            ) : (
              <Btn v="gold" sm onClick={onAuth}>
                <i className="fa-solid fa-arrow-right-to-bracket" /> {t('nav.login')}
              </Btn>
            ))}

          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: '1.4rem',
                cursor: 'pointer',
                padding: '0.5rem',
              }}
            >
              <i
                className={`fa-solid ${
                  mobileMenuOpen ? 'fa-xmark' : 'fa-bars'
                }`}
              ></i>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {isMobile && mobileMenuOpen && (
        <div
          style={{
            background: C.gray,
            borderTop: `1px solid rgba(255,255,255,.1)`,
            padding: '1rem 1.5rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {links.map(([p, l, icon]) => (
            <button
              key={p}
              onClick={() => {
                go(p);
                setMobileMenuOpen(false);
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                background: page === p ? `${C.gold}18` : 'none',
                border: 'none',
                padding: '.75rem 1rem',
                borderRadius: 8,
                color: page === p ? C.gold : '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <i className={`fa-solid ${icon}`} style={{ width: '18px' }} />
              {l}
            </button>
          ))}
          <div style={{ padding: '.75rem 1rem 0' }}>
            <LanguageSwitcher />
          </div>
          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.08)',
              marginTop: '0.75rem',
              paddingTop: '1rem',
            }}
          >
            {auth.isLoggedIn ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                }}
              >
                <span
                  style={{ fontSize: '.8rem', color: 'rgba(255,255,255,0.6)' }}
                >
                  {t('nav.loggedInAs')} <strong>{auth.user}</strong>
                </span>
                <Btn
                  v="ghost"
                  full
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('nav.logout')}
                </Btn>
              </div>
            ) : (
              <Btn
                v="gold"
                full
                onClick={() => {
                  onAuth();
                  setMobileMenuOpen(false);
                }}
              >
                <i className="fa-solid fa-arrow-right-to-bracket" /> {t('nav.loginNow')}
              </Btn>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

/* ══ Auth Modal ═════════════════════════════════════════════════ */
function AuthModal({ onClose, onSuccess }) {
  const C = useT();
  const { t } = useTranslation();
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const doLogin = async () => {
    setErr('');
    if (!validEmail(email)) {
      setErr(t('authModal.errEmailRequired'));
      return;
    }
    if (pw.length < 3) {
      setErr(t('authModal.errPasswordShort3'));
      return;
    }
    setBusy(true);
    try {
      await signInWithPassword(email, pw);
      onSuccess();
    } catch (e) {
      setErr(e.message || t('authModal.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const doReg = async () => {
    setErr('');
    if (!validEmail(email)) {
      setErr(t('authModal.errEmailRequired'));
      return;
    }
    if (!name.trim()) {
      setErr(t('authModal.errNameRequired'));
      return;
    }
    if (pw.length < 6) {
      setErr(t('authModal.errPasswordShort6'));
      return;
    }
    setBusy(true);
    try {
      await signUpWithPassword(email, pw, name.trim());
      setOk(t('authModal.registerSuccess'));
      setTimeout(onClose, 3500);
    } catch (e) {
      setErr(e.message || t('authModal.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const doGoogle = async () => {
    setErr('');
    setBusy(true);
    try {
      await signInWithGoogle();
      // Browser will redirect away to Google, then back to the app.
    } catch (e) {
      setErr(e.message || t('authModal.errGeneric'));
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.65)',
        backdropFilter: 'blur(5px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          borderRadius: 24,
          overflow: 'hidden',
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 24px 64px rgba(0,0,0,.4)',
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        <div
          style={{
            background: C.dark,
            padding: '1.75rem',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: C.gold,
            }}
          />
          <h3
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              color: '#fff',
              fontSize: '1.25rem',
              marginBottom: '.25rem',
            }}
          >
            {tab === 'login' ? t('authModal.loginTitle') : t('authModal.registerTitle')}
          </h3>
          <p
            style={{
              fontSize: '.72rem',
              color: C.gold,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('authModal.subtitle')}
          </p>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1.5rem',
              right: '1.5rem',
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,.5)',
              fontSize: '1.2rem',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '1.75rem' }}>
          <div
            style={{
              display: 'flex',
              gap: '.25rem',
              background: C.bg,
              borderRadius: 10,
              padding: '.25rem',
              marginBottom: '1.25rem',
            }}
          >
            {['login', 'register'].map((t2) => (
              <button
                key={t2}
                onClick={() => setTab(t2)}
                style={{
                  flex: 1,
                  padding: '.5rem',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '.8rem',
                  fontWeight: 700,
                  background: tab === t2 ? C.white : 'transparent',
                  color: tab === t2 ? C.dark : C.muted,
                  boxShadow: tab === t2 ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
                  transition: 'all .18s',
                }}
              >
                {t2 === 'login' ? t('authModal.tabLogin') : t('authModal.tabRegister')}
              </button>
            ))}
          </div>
          {ok ? (
            <div
              style={{
                textAlign: 'center',
                color: C.green,
                padding: '1.5rem 0',
                fontWeight: 600,
                fontSize: '0.9rem',
                lineHeight: 1.5,
              }}
            >
              <i
                className="fa-solid fa-circle-check"
                style={{
                  fontSize: '2rem',
                  display: 'block',
                  marginBottom: '0.75rem',
                }}
              />
              {ok}
            </div>
          ) : (
            <>
              <Btn v="outline" full onClick={doGoogle} disabled={busy} sx={{ marginBottom: '1.25rem' }}>
                <i className="fa-brands fa-google" /> {t('authModal.googleCta')}
              </Btn>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '.75rem',
                  margin: '0 0 1.25rem',
                  color: C.muted,
                  fontSize: '.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '.08em',
                }}
              >
                <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,.08)' }} />
                {t('authModal.dividerOr')}
                <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,.08)' }} />
              </div>
              {tab === 'register' && (
                <FieldInput
                  label={t('authModal.nameLabel')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('authModal.namePlaceholder')}
                />
              )}
              <FieldInput
                label={t('authModal.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('authModal.emailPlaceholder')}
              />
              <FieldInput
                label={t('authModal.passwordLabel')}
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={
                  tab === 'register'
                    ? t('authModal.passwordPlaceholderRegister')
                    : t('authModal.passwordPlaceholderLogin')
                }
              />
              {err && (
                <p
                  style={{
                    color: C.red,
                    fontSize: '.78rem',
                    marginBottom: '.75rem',
                    fontWeight: 600,
                  }}
                >
                  <i className="fa-solid fa-circle-exclamation" /> {err}
                </p>
              )}
              <Btn
                v="gold"
                full
                disabled={busy}
                onClick={tab === 'login' ? doLogin : doReg}
              >
                {busy
                  ? t('authModal.signingIn')
                  : tab === 'login'
                  ? t('authModal.submitLogin')
                  : t('authModal.submitRegister')}
              </Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══ Event Registration Modal ══ */
function RegModal({ event, onClose }) {
  const C = useT();
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  if (!event) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.65)',
        backdropFilter: 'blur(5px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          borderRadius: 24,
          overflow: 'hidden',
          width: '100%',
          maxWidth: 500,
          boxShadow: '0 24px 64px rgba(0,0,0,.4)',
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        <div
          style={{
            background: C.dark,
            padding: '1.75rem',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: C.gold,
            }}
          />
          <h3
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              color: '#fff',
              fontSize: '1.25rem',
              marginBottom: '.2rem',
            }}
          >
            {t('regModal.title')}
          </h3>
          <p style={{ fontSize: '.78rem', color: C.gold, fontWeight: 600 }}>
            {event.title}
          </p>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1.5rem',
              right: '1.5rem',
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,.5)',
              fontSize: '1.2rem',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        {done ? (
          <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                background: '#DCFCE7',
                color: C.green,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.8rem',
                margin: '0 auto 1.25rem',
              }}
            >
              <i className="fa-solid fa-circle-check" />
            </div>
            <h3
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 800,
                color: C.dark,
                marginBottom: '.5rem',
              }}
            >
              {t('regModal.successTitle')}
            </h3>
            <p
              style={{
                color: C.muted,
                fontSize: '.9rem',
                marginBottom: '1.5rem',
                lineHeight: 1.5,
              }}
            >
              {t('regModal.successText')}
            </p>
            <Btn v="dark" onClick={onClose}>
              {t('regModal.close')}
            </Btn>
          </div>
        ) : (
          <div style={{ padding: '1.75rem' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
              }}
            >
              <FieldInput label={t('regModal.firstName')} placeholder={t('regModal.firstNamePh')} />
              <FieldInput label={t('regModal.lastName')} placeholder={t('regModal.lastNamePh')} />
            </div>
            <FieldInput
              label={t('regModal.email')}
              type="email"
              placeholder={t('regModal.emailPh')}
            />
            <FieldSelect label={t('regModal.locationLabel')}>
              {t('regModal.locationOptions', { returnObjects: true }).map((v) => (
                <option key={v}>{v}</option>
              ))}
            </FieldSelect>
            <div
              style={{
                display: 'flex',
                gap: '.75rem',
                justifyContent: 'flex-end',
                paddingTop: '1rem',
                borderTop: `1px solid ${C.bg}`,
              }}
            >
              <Btn v="muted" sm onClick={onClose}>
                {t('regModal.cancel')}
              </Btn>
              <Btn v="gold" onClick={() => setDone(true)}>
                <i className="fa-solid fa-calendar-check" /> {t('regModal.confirm')}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══ Animated Counter ══ */
// Counts up from its previous value to `value` once the element has
// scrolled into view. Re-animates whenever `value` changes afterwards,
// which also covers values that only resolve after an async data fetch.
function AnimatedCounter({ value, suffix = '', duration = 1600 }) {
  const [display, setDisplay] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const prevTarget = useRef(0);
  const target = Number(value) || 0;

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || prevTarget.current === target) return undefined;
    const from = prevTarget.current;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        prevTarget.current = target;
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [visible, target, duration]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

/* ══ Hero Section ══ */
function Hero({ go, data, memberCount, onAuth }) {
  const C = useT();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 992;

  return (
    <section
      style={{
        background: C.dark,
        color: '#fff',
        padding: isMobile ? '4rem 1.5rem' : '6rem 2rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${C.gold}15 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '-10rem',
          right: '-10rem',
          width: 380,
          height: 380,
          background: C.gold,
          borderRadius: '50%',
          filter: 'blur(120px)',
          opacity: 0.12,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-10rem',
          left: '-10rem',
          width: 320,
          height: 320,
          background: C.gold,
          borderRadius: '50%',
          filter: 'blur(120px)',
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr',
          gap: '3.5rem',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.4rem',
              fontSize: '.72rem',
              fontWeight: 600,
              background: `${C.gold}18`,
              color: C.gold,
              border: `1px solid ${C.gold}30`,
              padding: '.4rem .95rem',
              borderRadius: 20,
              marginBottom: '1.5rem',
            }}
          >
            <i className="fa-solid fa-people-group" /> {t('hero.badge')}
          </span>
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(2.2rem, 5vw, 3.4rem)',
              lineHeight: 1.15,
              marginBottom: '1.25rem',
            }}
          >
            {t('hero.title1')}
            <br />
            <span style={{ color: C.gold }}>{t('hero.title2')}</span>
          </h1>
          <p
            style={{
              color: 'rgba(255,255,255,.7)',
              fontSize: '1.05rem',
              lineHeight: 1.75,
              maxWidth: 540,
              marginBottom: '2.25rem',
            }}
          >
            {t('hero.desc')}
          </p>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              marginBottom: '3rem',
            }}
          >
            <Btn v="gold" onClick={() => go('gebete')}>
              <i className="fa-solid fa-compass" /> {t('hero.ctaPrayer')}
            </Btn>
            <Btn v="ghost" onClick={() => go('ueber-uns')}>
              {t('hero.ctaMore')} <i className="fa-solid fa-arrow-right" />
            </Btn>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '1rem',
              paddingTop: '2rem',
              borderTop: '1px solid rgba(255,255,255,.12)',
            }}
          >
            {[
              [memberCount, '+', t('hero.statMembers')],
              [data.locations.length, '', t('hero.statLocations')],
              [1, '', t('hero.statVision')],
            ].map(([n, suffix, l], i) => (
              <div key={i}>
                <div
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(1.7rem, 4vw, 2.4rem)',
                    color: i % 2 === 0 ? C.gold : '#fff',
                    lineHeight: 1,
                  }}
                >
                  <AnimatedCounter value={n} suffix={suffix} />
                </div>
                <div
                  style={{
                    fontSize: '.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '.08em',
                    color: 'rgba(255,255,255,.45)',
                    marginTop: '.35rem',
                  }}
                >
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Highlight quote widget */}
        <div
          style={{
            background: `linear-gradient(135deg, ${C.gray}, ${C.dark}f5)`,
            border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 24,
            padding: '2.25rem',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,.35)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: C.gold,
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyTarget: 'space-between',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
            }}
          >
            <i
              className="fa-solid fa-quote-left"
              style={{ fontSize: '2rem', color: C.gold, opacity: 0.35 }}
            />
            <span
              style={{
                fontSize: '.68rem',
                color: `${C.gold}aa`,
                fontWeight: 600,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
              }}
            >
              #KraftquelleArbeitsplatz
            </span>
          </div>
          <blockquote
            style={{
              fontSize: '1.05rem',
              lineHeight: 1.8,
              color: 'rgba(255,255,255,.9)',
              marginBottom: '1.75rem',
              fontStyle: 'italic',
              padding: 0,
              margin: 0,
            }}
          >
            {t('hero.quote')}
          </blockquote>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '.75rem',
              paddingTop: '1.25rem',
              borderTop: '1px solid rgba(255,255,255,.08)',
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                background: `${C.gold}20`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyTarget: 'center',
                justifyContent: 'center',
                color: C.gold,
                fontWeight: 700,
                fontSize: '.9rem',
                flexShrink: 0,
              }}
            >
              BG
            </div>
            <div>
              <div
                style={{ fontWeight: 700, color: '#fff', fontSize: '.92rem' }}
              >
                {t('hero.quoteAuthor')}
              </div>
              <div
                style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.45)' }}
              >
                {t('hero.quoteRole')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══ About Section ══ */
function AboutSection() {
  const C = useT();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 992;

  const cards = [
    {
      icon: 'fa-hands-holding-child',
      title: t('about.card1Title'),
      text: t('about.card1Text'),
    },
    {
      icon: 'fa-heart-pulse',
      title: t('about.card2Title'),
      text: t('about.card2Text'),
    },
    {
      icon: 'fa-circle-nodes',
      title: t('about.card3Title'),
      text: t('about.card3Text'),
    },
  ];

  return (
    <Reveal as="section" style={{ background: C.white, padding: '5.5rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <SectionHeader
          eyebrow={t('about.eyebrow')}
          title={t('about.title')}
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.75rem',
          }}
        >
          {cards.map((c, i) => (
            <Reveal
              key={i}
              delay={i * 90}
              style={{
                background: C.bg,
                border: '1px solid rgba(0,0,0,.06)',
                borderRadius: 20,
                padding: '2.25rem',
                boxShadow: '0 4px 12px rgba(0,0,0,.02)',
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  background: `${C.gold}18`,
                  borderRadius: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyTarget: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem',
                  color: C.gold,
                  marginBottom: '1.5rem',
                }}
              >
                <i className={`fa-solid ${c.icon}`} />
              </div>
              <h3
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 700,
                  color: C.dark,
                  marginBottom: '.75rem',
                  fontSize: '1.15rem',
                }}
              >
                {c.title}
              </h3>
              <p
                style={{
                  color: C.muted,
                  fontSize: '.9rem',
                  lineHeight: 1.75,
                  margin: 0,
                }}
              >
                {c.text}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

/* ══ Location Finder ══ */
function LocationFinder({ data }) {
  const C = useT();
  const { t } = useTranslation();
  const [sel, setSel] = useState(data.locations[0]?.id || '');
  const { width } = useWindowSize();
  const isMobile = width < 992;

  const loc = data.locations.find((l) => l.id === sel) || data.locations[0];

  return (
    <Reveal as="section" style={{ background: C.bg, padding: '5.5rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <SectionHeader
          eyebrow={t('locationFinder.eyebrow')}
          title={t('locationFinder.title')}
          sub={t('locationFinder.sub')}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '280px 1fr',
            gap: '2rem',
            background: C.white,
            borderRadius: 24,
            border: '1px solid rgba(0,0,0,.06)',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0,0,0,.04)',
          }}
        >
          {/* Left Navigation bar */}
          <div style={{ background: C.dark, padding: '1.75rem' }}>
            <div
              style={{
                fontSize: '.68rem',
                textTransform: 'uppercase',
                letterSpacing: '.14em',
                fontWeight: 700,
                color: `${C.gold}aa`,
                marginBottom: '1.25rem',
              }}
            >
              {t('locationFinder.sidebarTitle')}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                gap: '.5rem',
                overflowX: isMobile ? 'auto' : 'visible',
                paddingBottom: isMobile ? '.5rem' : 0,
              }}
            >
              {data.locations.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSel(l.id)}
                  style={{
                    minWidth: isMobile ? '200px' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '.75rem',
                    padding: '.85rem 1rem',
                    borderRadius: 12,
                    border:
                      sel === l.id
                        ? `2px solid ${C.gold}`
                        : '2px solid transparent',
                    background: sel === l.id ? `${C.gold}12` : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all .18s',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: sel === l.id ? C.gold : C.gray,
                      display: 'flex',
                      alignItems: 'center',
                      justifyTarget: 'center',
                      justifyContent: 'center',
                      fontSize: '.85rem',
                      fontWeight: 800,
                      color: sel === l.id ? C.dark : 'rgba(255,255,255,.6)',
                      flexShrink: 0,
                    }}
                  >
                    {l.name.trim().charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '.85rem',
                        fontWeight: 700,
                        color: sel === l.id ? C.gold : 'rgba(255,255,255,.85)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {l.name}
                    </div>
                    <div
                      style={{
                        fontSize: '.68rem',
                        color: 'rgba(255,255,255,.4)',
                        marginTop: '.15rem',
                      }}
                    >
                      {l.time.split(',')[0]}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right Detail sheet */}
          {loc && (
            <div style={{ padding: isMobile ? '2rem 1.5rem' : '3rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  paddingBottom: '1.5rem',
                  borderBottom: `1px solid ${C.bg}`,
                }}
              >
                <div>
                  <h3
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 800,
                      color: C.dark,
                      fontSize: '1.5rem',
                      margin: 0,
                    }}
                  >
                    {loc.name}
                  </h3>
                  <p
                    style={{
                      color: C.muted,
                      fontSize: '.85rem',
                      margin: '0.25rem 0 0',
                    }}
                  >
                    {loc.dept ? `${t('locationFinder.deptPrefix')}: ${loc.dept}` : ''}
                  </p>
                </div>
                <span
                  style={{
                    background: `${C.gold}18`,
                    color: C.goldH,
                    fontSize: '.72rem',
                    fontWeight: 700,
                    padding: '.3rem .85rem',
                    borderRadius: 20,
                  }}
                >
                  <i className="fa-solid fa-location-dot" />{' '}
                  {loc.id === 'online' ? t('locationFinder.virtual') : t('locationFinder.onSite')}
                </span>
              </div>

              <p
                style={{
                  color: C.muted,
                  fontSize: '.92rem',
                  lineHeight: 1.8,
                  marginBottom: '2rem',
                  margin: 0,
                }}
              >
                {loc.desc}
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1.25rem',
                  margin: '2rem 0',
                }}
              >
                <div
                  style={{
                    background: C.bg,
                    borderRadius: 16,
                    padding: '1.25rem',
                    border: '1px solid rgba(0,0,0,.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.12em',
                      fontWeight: 700,
                      color: C.muted,
                      marginBottom: '.35rem',
                    }}
                  >
                    <i className="fa-regular fa-clock" /> {t('locationFinder.timeLabel')}
                  </div>
                  <div
                    style={{ fontWeight: 700, fontSize: '1rem', color: C.dark }}
                  >
                    {loc.time}
                  </div>
                </div>
                <div
                  style={{
                    background: C.bg,
                    borderRadius: 16,
                    padding: '1.25rem',
                    border: '1px solid rgba(0,0,0,.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.12em',
                      fontWeight: 700,
                      color: C.muted,
                      marginBottom: '.35rem',
                    }}
                  >
                    <i className="fa-solid fa-door-open" /> {t('locationFinder.roomLabel')}
                  </div>
                  <div
                    style={{ fontWeight: 700, fontSize: '1rem', color: C.dark }}
                  >
                    {loc.room}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '.85rem',
                  background: `${C.bg}60`,
                  padding: '1rem 1.25rem',
                  borderRadius: 16,
                  border: '1px solid rgba(0,0,0,0.02)',
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    background: `${C.gold}22`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyTarget: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1rem',
                    color: C.gold,
                    flexShrink: 0,
                  }}
                >
                  {loc.initial}
                </div>
                <div>
                  <div style={{ fontSize: '.72rem', color: C.muted }}>
                    {t('locationFinder.contactPrefix')}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: C.dark,
                      fontSize: '.9rem',
                    }}
                  >
                    {loc.contact} {loc.dept ? `(${loc.dept})` : ''}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
}

/* ══ Pillars ══ */
function Pillars() {
  const C = useT();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 992;

  const items = [
    {
      abbr: null,
      icon: 'fa-solid fa-people-group',
      title: t('pillars.item1Title'),
      text: t('pillars.item1Text'),
      gold: true,
    },
    {
      abbr: null,
      icon: 'fa-hands-praying',
      title: t('pillars.item2Title'),
      text: t('pillars.item2Text'),
      gold: false,
    },
    {
      abbr: null,
      icon: 'fa-bolt',
      title: t('pillars.item3Title'),
      text: t('pillars.item3Text'),
      gold: true,
    },
  ];

  return (
    <Reveal as="section" style={{ background: C.white, padding: '5.5rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <SectionHeader eyebrow={t('pillars.eyebrow')} title={t('pillars.title')} />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.75rem',
          }}
        >
          {items.map((p, i) => (
            <Reveal
              key={i}
              delay={i * 90}
              style={{
                background: C.bg,
                borderRadius: 24,
                padding: '2.5rem 2rem',
                border: '1px solid rgba(0,0,0,.05)',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 18,
                  background: p.gold ? C.gold : C.dark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyTarget: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: p.abbr ? '1rem' : '1.6rem',
                  fontWeight: 800,
                  color: p.gold ? C.dark : C.gold,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                }}
              >
                {p.abbr || <i className={`fa-solid ${p.icon}`} />}
              </div>
              <h3
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 800,
                  color: C.dark,
                  marginBottom: '.75rem',
                  fontSize: '1.2rem',
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  color: C.muted,
                  fontSize: '.9rem',
                  lineHeight: 1.75,
                  margin: 0,
                }}
              >
                {p.text}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

/* ══ Prayer Wall ══ */
function PrayerWall({ auth, prayers, likedIds, onSubmit, onToggleLike, onAuth }) {
  const C = useT();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 992;

  const [text, setText] = useState('');
  const [author, setAuthor] = useState('');
  const [cat, setCat] = useState('work');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const approved = prayers.filter((p) => p.approved);
  const categories = ['work', 'family', 'health', 'gratitude'];

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit({ text: text.trim(), author: author.trim(), cat });
      setText('');
      setAuthor('');
      setSent(true);
      setTimeout(() => setSent(false), 6000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Reveal
      as="section"
      id="gebetswand"
      style={{
        background: C.wall,
        padding: '5.5rem 1.5rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${C.gold}12 1px, transparent 1px)`,
          backgroundSize: '20px 20px',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1.3fr',
          gap: '4rem',
          alignItems: 'start',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div>
          <span
            style={{
              fontSize: '.68rem',
              textTransform: 'uppercase',
              letterSpacing: '.18em',
              fontWeight: 700,
              color: C.gold,
              display: 'block',
              marginBottom: '.5rem',
            }}
          >
            {t('prayerWall.eyebrow')}
          </span>
          <h2
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              color: '#fff',
              fontSize: 'clamp(1.8rem, 4.5vw, 2.4rem)',
              marginBottom: '1rem',
              lineHeight: 1.2,
            }}
          >
            {t('prayerWall.title')}
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,.6)',
              fontSize: '.92rem',
              lineHeight: 1.8,
              marginBottom: '2rem',
            }}
          >
            {t('prayerWall.desc')}
          </p>

          {!auth.isLoggedIn ? (
            <div
              style={{
                background: `${C.gold}10`,
                border: `1.5px solid ${C.gold}30`,
                borderRadius: 20,
                padding: '2rem',
                textAlign: 'center',
                boxShadow: '0 12px 32px rgba(0,0,0,.15)',
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>
                🔒
              </div>
              <h4
                style={{
                  color: '#fff',
                  margin: '0 0 0.5rem',
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.1rem',
                }}
              >
                {t('prayerWall.lockedTitle')}
              </h4>
              <p
                style={{
                  color: 'rgba(255,255,255,.7)',
                  fontSize: '.85rem',
                  marginBottom: '1.25rem',
                  lineHeight: 1.6,
                }}
              >
                {t('prayerWall.lockedText')}
              </p>
              <Btn v="gold" onClick={onAuth}>
                <i className="fa-solid fa-arrow-right-to-bracket" /> {t('prayerWall.loginCta')}
              </Btn>
            </div>
          ) : (
            <>
              {sent && (
                <div
                  style={{
                    background: 'rgba(16,185,129,.15)',
                    border: '1px solid rgba(16,185,129,.3)',
                    borderRadius: 12,
                    padding: '1rem',
                    marginBottom: '1.25rem',
                    color: '#6EE7B7',
                    fontSize: '.82rem',
                    fontWeight: 600,
                  }}
                >
                  <i className="fa-solid fa-circle-check" /> {t('prayerWall.sentBanner')}
                </div>
              )}
              <div
                style={{
                  background: C.gray,
                  borderRadius: 20,
                  padding: '2rem',
                  border: '1px solid rgba(255,255,255,.08)',
                  boxShadow: '0 12px 32px rgba(0,0,0,.2)',
                }}
              >
                <div style={{ marginBottom: '1rem' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.1em',
                      fontWeight: 700,
                      color: 'rgba(255,255,255,.55)',
                      marginBottom: '.4rem',
                    }}
                  >
                    {t('prayerWall.authorLabel')}
                  </label>
                  <input
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder={t('prayerWall.authorPlaceholder')}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,.06)',
                      border: '1px solid rgba(255,255,255,.12)',
                      borderRadius: 10,
                      padding: '.75rem 1rem',
                      color: '#fff',
                      fontFamily: 'inherit',
                      fontSize: '.875rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.1em',
                      fontWeight: 700,
                      color: 'rgba(255,255,255,.55)',
                      marginBottom: '.4rem',
                    }}
                  >
                    {t('prayerWall.categoryLabel')}
                  </label>
                  <select
                    value={cat}
                    onChange={(e) => setCat(e.target.value)}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,.06)',
                      border: '1px solid rgba(255,255,255,.12)',
                      borderRadius: 10,
                      padding: '.75rem 1rem',
                      color: '#fff',
                      fontFamily: 'inherit',
                      fontSize: '.875rem',
                      outline: 'none',
                    }}
                  >
                    {categories.map((c) => (
                      <option key={c} value={c} style={{ background: C.gray }}>
                        {t(`prayerWall.categories.${c}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.1em',
                      fontWeight: 700,
                      color: 'rgba(255,255,255,.55)',
                      marginBottom: '.4rem',
                    }}
                  >
                    {t('prayerWall.requestLabel')}
                  </label>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={3}
                    placeholder={t('prayerWall.requestPlaceholder')}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,.06)',
                      border: '1px solid rgba(255,255,255,.12)',
                      borderRadius: 10,
                      padding: '.75rem 1rem',
                      color: '#fff',
                      fontFamily: 'inherit',
                      fontSize: '.875rem',
                      outline: 'none',
                      resize: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <Btn v="gold" full onClick={submit} disabled={!text.trim() || busy}>
                  <i className="fa-regular fa-paper-plane" /> {t('prayerWall.submit')}
                </Btn>
              </div>
            </>
          )}
        </div>

        {/* Prayers Feed */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.25rem',
            }}
          >
            <h3
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                color: '#fff',
                fontSize: '1.2rem',
                margin: 0,
              }}
            >
              {t('prayerWall.feedTitle')}
            </h3>
            <span
              style={{
                fontSize: '.72rem',
                color: 'rgba(255,255,255,.45)',
                background: 'rgba(255,255,255,.07)',
                padding: '.35rem .85rem',
                borderRadius: 20,
              }}
            >
              <i
                className="fa-solid fa-hands-praying"
                style={{ color: C.gold }}
              />{' '}
              {t('prayerWall.feedBadge')}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              maxHeight: 530,
              overflowY: 'auto',
              paddingRight: '0.25rem',
            }}
          >
            {approved.map((p) => (
              <div
                key={p.id}
                style={{
                  background: C.gray,
                  borderRadius: 18,
                  padding: '1.5rem',
                  border: '1px solid rgba(255,255,255,.07)',
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '1.25rem',
                    right: '1.25rem',
                    background: `${CAT_COLORS[p.cat]}15`,
                    color: CAT_COLORS[p.cat],
                    fontSize: '.68rem',
                    fontWeight: 700,
                    padding: '.25rem .75rem',
                    borderRadius: 20,
                    border: `1px solid ${CAT_COLORS[p.cat]}25`,
                  }}
                >
                  {t(`prayerWall.categories.${p.cat}`)}
                </span>
                <div
                  style={{
                    fontSize: '.75rem',
                    color: 'rgba(255,255,255,.4)',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                  }}
                >
                  {t('prayerWall.submittedBy')}{' '}
                  <strong style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {p.author || t('prayerWall.anonymous')}
                  </strong>
                </div>
                <p
                  style={{
                    color: 'rgba(255,255,255,.85)',
                    fontSize: '.9rem',
                    lineHeight: 1.75,
                    marginBottom: '1.25rem',
                    paddingRight: '5rem',
                    margin: 0,
                  }}
                >
                  {p.text}
                </p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '.85rem',
                    borderTop: '1px solid rgba(255,255,255,.07)',
                  }}
                >
                  <span
                    style={{
                      fontSize: '.72rem',
                      color: 'rgba(255,255,255,.35)',
                    }}
                  >
                    <i className="fa-regular fa-clock" /> {p.date}
                  </span>
                  <button
                    onClick={() => auth.isLoggedIn && onToggleLike(p.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '.4rem',
                      background: likedIds[p.id]
                        ? `${C.gold}25`
                        : 'rgba(255,255,255,.05)',
                      border: 'none',
                      borderRadius: 8,
                      padding: '.45rem .9rem',
                      cursor: auth.isLoggedIn ? 'pointer' : 'not-allowed',
                      color: C.gold,
                      fontSize: '.75rem',
                      fontWeight: 700,
                      fontFamily: 'inherit',
                      transition: 'all .15s ease-in-out',
                    }}
                  >
                    <i
                      className={
                        likedIds[p.id]
                          ? 'fa-solid fa-heart'
                          : 'fa-regular fa-heart'
                      }
                    />{' '}
                    {likedIds[p.id] ? t('prayerWall.prayed') : t('prayerWall.prayMore')} (
                    {p.prayCount})
                  </button>
                </div>
              </div>
            ))}
            {approved.length === 0 && (
              <p
                style={{
                  color: 'rgba(255,255,255,.4)',
                  textAlign: 'center',
                  padding: '2rem',
                }}
              >
                {t('prayerWall.emptyFeed')}
              </p>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

/* ══ Events Section ══ */
function EventsSection({ data }) {
  const C = useT();
  const { t } = useTranslation();
  const [regEvent, setRegEvent] = useState(null);
  const { width } = useWindowSize();
  const isMobile = width < 992;

  return (
    <Reveal as="section" style={{ background: C.bg, padding: '5.5rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <SectionHeader
          eyebrow={t('events.eyebrow')}
          title={t('events.title')}
          sub={t('events.sub')}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '2rem',
            alignItems: 'stretch',
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}
          >
            {data.events
              .filter((e) => e.active)
              .map((ev, i) => (
                <Reveal
                  key={ev.id}
                  delay={i * 80}
                  style={{
                    background: C.white,
                    borderRadius: 20,
                    padding: '1.75rem',
                    border: '1px solid rgba(0,0,0,.06)',
                    boxShadow: '0 4px 15px rgba(0,0,0,.03)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyTarget: 'space-between',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <span
                      style={{
                        display: 'inline-block',
                        background:
                          ev.type === 'Haupt-Event' ? `${C.gold}20` : `${C.bg}`,
                        color: ev.type === 'Haupt-Event' ? C.dark : C.muted,
                        fontSize: '.72rem',
                        fontWeight: 700,
                        padding: '.25rem .75rem',
                        borderRadius: 20,
                        marginBottom: '1rem',
                      }}
                    >
                      {ev.type}
                    </span>
                    <h3
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 800,
                        color: C.dark,
                        marginBottom: '.5rem',
                        fontSize: '1.2rem',
                        margin: 0,
                      }}
                    >
                      {ev.title}
                    </h3>
                    <p
                      style={{
                        color: C.muted,
                        fontSize: '.85rem',
                        lineHeight: 1.6,
                        marginBottom: '1.5rem',
                        marginTop: '.25rem',
                      }}
                    >
                      {ev.desc}
                    </p>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '1.25rem',
                      borderTop: `1px solid ${C.bg}`,
                      flexWrap: 'wrap',
                      gap: '.75rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        fontSize: '.78rem',
                        color: C.muted,
                      }}
                    >
                      <span>
                        <i
                          className="fa-regular fa-calendar"
                          style={{ color: C.gold }}
                        />{' '}
                        {ev.date}
                      </span>
                      <span>
                        <i
                          className="fa-regular fa-clock"
                          style={{ color: C.gold }}
                        />{' '}
                        {ev.time}
                      </span>
                    </div>
                    <Btn v="dark" sm onClick={() => setRegEvent(ev)}>
                      {t('events.registerCta')}{' '}
                      <i className="fa-solid fa-angle-right" />
                    </Btn>
                  </div>
                </Reveal>
              ))}
          </div>

          {/* Testimonial Quote panel */}
          <div
            style={{
              background: C.dark,
              borderRadius: 24,
              padding: '2.5rem',
              border: '1px solid rgba(255,255,255,.08)',
              boxShadow: '0 16px 48px rgba(0,0,0,.25)',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyTarget: 'space-between',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: '-4rem',
                right: '-4rem',
                width: 220,
                height: 220,
                background: C.gold,
                borderRadius: '50%',
                filter: 'blur(80px)',
                opacity: 0.08,
              }}
            />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1.25rem',
                }}
              >
                <i
                  className="fa-regular fa-message"
                  style={{
                    fontSize: '2rem',
                    color: C.gold,
                    flexShrink: 0,
                  }}
                />
                <h3
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 800,
                    color: '#fff',
                    fontSize: '1.3rem',
                    lineHeight: 1.45,
                    margin: 0,
                  }}
                >
                  {t('events.testimonialQuote')}
                </h3>
              </div>
              <p
                style={{
                  color: 'rgba(255,255,255,.65)',
                  fontSize: '.9rem',
                  lineHeight: 1.8,
                  marginBottom: '2rem',
                  margin: 0,
                }}
              >
                {t('events.testimonialText')}
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '.85rem',
                paddingTop: '1.5rem',
                borderTop: '1px solid rgba(255,255,255,.08)',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  background: `${C.gold}18`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyTarget: 'center',
                  justifyContent: 'center',
                  color: C.gold,
                  fontWeight: 700,
                  fontSize: '.9rem',
                  flexShrink: 0,
                }}
              >
                T
              </div>
              <div>
                <div
                  style={{ fontWeight: 700, color: '#fff', fontSize: '.92rem' }}
                >
                  {t('events.testimonialName')}
                </div>
                <div
                  style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.45)' }}
                >
                  {t('events.testimonialRole')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RegModal event={regEvent} onClose={() => setRegEvent(null)} />
    </Reveal>
  );
}

/* ══ Contact Section ══ */
function ContactSection() {
  const C = useT();
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);

  return (
    <Reveal as="section" style={{ background: C.white, padding: '5.5rem 1.5rem' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div
          style={{
            background: C.bg,
            borderRadius: 24,
            padding: '3rem 2rem',
            border: '1px solid rgba(0,0,0,.06)',
          }}
        >
          <SectionHeader
            eyebrow={t('contact.eyebrow')}
            title={t('contact.title')}
            sub={t('contact.sub')}
          />

          {sent ? (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  background: '#DCFCE7',
                  color: C.green,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyTarget: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  margin: '0 auto 1.25rem',
                }}
              >
                <i className="fa-solid fa-circle-check" />
              </div>
              <h3
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 800,
                  color: C.dark,
                }}
              >
                {t('contact.successTitle')}
              </h3>
              <p style={{ color: C.muted, marginTop: '.5rem' }}>
                {t('contact.successText')}
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                }}
              >
                <FieldInput
                  label={t('contact.nameLabel')}
                  placeholder={t('contact.namePlaceholder')}
                />
                <FieldInput
                  label={t('contact.emailLabel')}
                  type="email"
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>
              <FieldTA
                label={t('contact.messageLabel')}
                rows={4}
                placeholder={t('contact.messagePlaceholder')}
              />
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <Btn v="dark" onClick={() => setSent(true)}>
                  <i className="fa-regular fa-paper-plane" /> {t('contact.submit')}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </Reveal>
  );
}

/* ══ Footer ══ */
function Footer({ go, showSpenden }) {
  const C = useT();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  return (
    <footer
      style={{
        background: C.dark,
        borderTop: `8px solid ${C.gold}`,
        padding: '4rem 1.5rem 2rem',
        color: 'rgba(255,255,255,0.7)',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile
            ? '1fr'
            : showSpenden
            ? '2fr 1fr 1.2fr'
            : '1fr auto',
          gap: '3.5rem',
          marginBottom: '3rem',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                background: C.gold,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyTarget: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                color: C.dark,
              }}
            >
              <i className="fa-solid fa-cross" />
            </div>
            <span
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 800,
                color: '#fff',
                fontSize: '1.1rem',
              }}
            >
              {t('footer.brand')}
            </span>
          </div>
          <p
            style={{
              fontSize: '.82rem',
              color: 'rgba(255,255,255,.45)',
              lineHeight: 1.8,
              maxWidth: 360,
              marginBottom: '1rem',
              margin: 0,
            }}
          >
            {t('footer.desc')}
          </p>
          <br />
          <p
            style={{
              fontSize: '.72rem',
              color: 'rgba(255,255,255,.25)',
              lineHeight: 1.6,
              maxWidth: 360,
              margin: 0,
            }}
          >
            {t('footer.disclaimer')}
          </p>
        </div>

        <div>
          <h4
            style={{
              fontWeight: 700,
              fontSize: '.75rem',
              textTransform: 'uppercase',
              letterSpacing: '.14em',
              color: C.gold,
              marginBottom: '1.25rem',
              margin: 0,
            }}
          >
            {t('footer.navTitle')}
          </h4>
          <br />
          {[
            ['home', t('footer.navHome')],
            ['gebete', t('footer.navPrayerWall')],
            ['ueber-uns', t('footer.navAbout')],
            ['faq', t('footer.navFaq')],
            ['impressum', t('footer.navImpressum')],
          ].map(([p, l]) => (
            <div key={p} style={{ marginBottom: '.6rem' }}>
              <button
                onClick={() => go(p)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,.5)',
                  fontSize: '.82rem',
                  fontFamily: 'inherit',
                  padding: 0,
                  transition: 'color .15s',
                }}
              >
                {l}
              </button>
            </div>
          ))}
        </div>

        {showSpenden && (
          <div>
            <h4
              style={{
                fontWeight: 700,
                fontSize: '.75rem',
                textTransform: 'uppercase',
                letterSpacing: '.14em',
                color: C.gold,
                marginBottom: '1.25rem',
                margin: 0,
              }}
            >
              {t('footer.fundingTitle')}
            </h4>
            <p
              style={{
                fontSize: '.82rem',
                color: 'rgba(255,255,255,.45)',
                lineHeight: 1.7,
                marginBottom: '1rem',
                margin: 0,
              }}
            >
              {t('footer.fundingText')}
            </p>
            <div
              style={{
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(255,255,255,.06)',
                borderRadius: 12,
                padding: '1rem',
                fontSize: '.75rem',
                color: 'rgba(255,255,255,.5)',
                lineHeight: 1.9,
              }}
            >
              <strong style={{ color: '#fff', display: 'block' }}>
                {t('footer.fundingBoxTitle')}
              </strong>
              IBAN:{' '}
              <span style={{ fontFamily: 'monospace' }}>
                DE89 1234 5678 9012 3456 78
              </span>
              <br />
              {t('footer.fundingPurpose')}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,.07)',
          paddingTop: '1.75rem',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            fontSize: '.78rem',
            color: 'rgba(255,255,255,.3)',
            marginBottom: '.5rem',
            margin: 0,
          }}
        >
          {t('footer.copyright')}
        </p>
        <br />
        <p
          style={{
            fontSize: '.65rem',
            color: 'rgba(255,255,255,.2)',
            maxWidth: 840,
            margin: '0 auto',
            lineHeight: 1.7,
          }}
        >
          {t('footer.trademark')}
        </p>
      </div>
    </footer>
  );
}

/* ══ Admin Dashboard ══ */
function AdminDashboard({
  data,
  refreshContent,
  prayers,
  onApprovePrayer,
  onDeletePrayer,
  themeIdx,
  onSelectTheme,
  showSpenden,
  onToggleShowSpenden,
  memberCount,
  onUpdateMemberCount,
}) {
  const C = useT();
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const tabs = [
    ['overview', t('admin.tabs.overview')],
    ['events', t('admin.tabs.events')],
    ['locations', t('admin.tabs.locations')],
    ['news', t('admin.tabs.news')],
    ['prayers', t('admin.tabs.prayers')],
    ['design', t('admin.tabs.design')],
  ];

  return (
    <div style={{ background: C.bg, minHeight: '90vh' }}>
      <div
        style={{
          background: C.dark,
          borderBottom: `4px solid ${C.gold}`,
          padding: isMobile ? '1.25rem 1rem 0' : '1.75rem 2rem 0',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p
            style={{
              fontSize: '.68rem',
              fontWeight: 700,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: `${C.gold}a0`,
              marginBottom: '1.25rem',
              margin: 0,
            }}
          >
            {t('admin.headerEyebrow')}
          </p>
          <br />
          <div
            style={{
              display: 'flex',
              gap: '.25rem',
              overflowX: 'auto',
              paddingBottom: '1px',
            }}
          >
            {tabs.map(([tid, l]) => (
              <button
                key={tid}
                onClick={() => setTab(tid)}
                style={{
                  padding: '.65rem 1.25rem',
                  borderRadius: '10px 10px 0 0',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '.82rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  background: tab === tid ? C.bg : 'rgba(255,255,255,.05)',
                  color: tab === tid ? C.dark : `${C.gold}bf`,
                  transition: 'all .18s ease-in-out',
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '2rem 1rem' : '3rem 2rem',
        }}
      >
        {tab === 'overview' && (
          <AdminOverview data={data} prayers={prayers} C={C} />
        )}
        {tab === 'events' && (
          <AdminCRUD type="events" items={data.events} onRefresh={refreshContent} />
        )}
        {tab === 'locations' && (
          <AdminCRUD
            type="locations"
            items={data.locations}
            onRefresh={refreshContent}
          />
        )}
        {tab === 'news' && (
          <AdminCRUD type="news" items={data.news} onRefresh={refreshContent} />
        )}
        {tab === 'prayers' && (
          <AdminPrayers
            prayers={prayers}
            onApprove={onApprovePrayer}
            onDelete={onDeletePrayer}
          />
        )}
        {tab === 'design' && (
          <AdminDesign
            themeIdx={themeIdx}
            onSelectTheme={onSelectTheme}
            showSpenden={showSpenden}
            onToggleShowSpenden={onToggleShowSpenden}
            memberCount={memberCount}
            onUpdateMemberCount={onUpdateMemberCount}
          />
        )}
      </div>
    </div>
  );
}

function AdminOverview({ data, prayers, C }) {
  const { t } = useTranslation();
  const kpis = [
    [
      t('admin.overview.kpiEvents'),
      data.events.filter((e) => e.active).length,
      C.gold,
      'fa-calendar-day',
    ],
    [
      t('admin.overview.kpiRegistrations'),
      data.events.reduce((s, e) => s + e.registered, 0),
      C.blue,
      'fa-users',
    ],
    [
      t('admin.overview.kpiLocations'),
      data.locations.length,
      C.green,
      'fa-location-dot',
    ],
    [
      t('admin.overview.kpiPendingPrayers'),
      prayers.filter((p) => !p.approved).length,
      C.red,
      'fa-hourglass-half',
    ],
  ];

  return (
    <div>
      <h2
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 800,
          color: C.dark,
          marginBottom: '1.5rem',
          fontSize: '1.6rem',
          margin: 0,
        }}
      >
        {t('admin.overview.title')}
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.25rem',
          margin: '1.5rem 0 2.5rem',
        }}
      >
        {kpis.map(([l, v, c, icon], i) => (
          <div
            key={i}
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '1.5rem',
              borderTop: `4px solid ${c}`,
              boxShadow: '0 4px 15px rgba(0,0,0,.03)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: '2.25rem',
                  fontWeight: 800,
                  color: c,
                  lineHeight: 1,
                }}
              >
                {v}
              </div>
              <div
                style={{
                  fontSize: '.8rem',
                  color: C.muted,
                  marginTop: '.5rem',
                  fontWeight: 600,
                }}
              >
                {l}
              </div>
            </div>
            <i
              className={`fa-solid ${icon}`}
              style={{ fontSize: '2rem', color: `${c}22` }}
            />
          </div>
        ))}
      </div>
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          padding: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,.03)',
          border: '1px solid rgba(0,0,0,0.02)',
        }}
      >
        <h3
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            color: C.dark,
            marginBottom: '1.25rem',
            fontSize: '1.2rem',
            margin: 0,
          }}
        >
          {t('admin.overview.logTitle')}
        </h3>
        {data.news.map((n) => (
          <div
            key={n.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '.85rem 0',
              borderBottom: '1px solid rgba(0,0,0,.05)',
              flexWrap: 'wrap',
              gap: '.5rem',
            }}
          >
            <span
              style={{ fontSize: '.875rem', color: C.text, fontWeight: 500 }}
            >
              {n.title}
            </span>
            <span
              style={{
                fontSize: '.7rem',
                fontWeight: 700,
                padding: '.2rem .75rem',
                borderRadius: 20,
                background:
                  n.status === 'published' ? `${C.green}18` : `${C.gold}18`,
                color: n.status === 'published' ? C.green : C.goldH,
              }}
            >
              {n.status === 'published'
                ? t('admin.overview.published')
                : t('admin.overview.draft')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══ Admin CRUD System ══ */
function getFields(t, type) {
  if (type === 'events') {
    return [
      { k: 'title', l: t('admin.fields.events.title.l'), full: 1 },
      {
        k: 'date',
        l: t('admin.fields.events.date.l'),
        ph: t('admin.fields.events.date.ph'),
      },
      {
        k: 'time',
        l: t('admin.fields.events.time.l'),
        ph: t('admin.fields.events.time.ph'),
      },
      {
        k: 'location',
        l: t('admin.fields.events.location.l'),
        ph: t('admin.fields.events.location.ph'),
      },
      { k: 'registered', l: t('admin.fields.events.registered.l'), type: 'number' },
      {
        k: 'type',
        l: t('admin.fields.events.type.l'),
        ph: t('admin.fields.events.type.ph'),
      },
      { k: 'desc', l: t('admin.fields.events.desc.l'), m: 1, full: 1 },
      { k: 'active', l: t('admin.fields.events.active.l'), type: 'cb' },
    ];
  }
  if (type === 'locations') {
    return [
      { k: 'name', l: t('admin.fields.locations.name.l'), full: 1 },
      {
        k: 'initial',
        l: t('admin.fields.locations.initial.l'),
        ph: t('admin.fields.locations.initial.ph'),
      },
      {
        k: 'dept',
        l: t('admin.fields.locations.dept.l'),
        ph: t('admin.fields.locations.dept.ph'),
      },
      {
        k: 'time',
        l: t('admin.fields.locations.time.l'),
        ph: t('admin.fields.locations.time.ph'),
      },
      {
        k: 'room',
        l: t('admin.fields.locations.room.l'),
        ph: t('admin.fields.locations.room.ph'),
      },
      {
        k: 'contact',
        l: t('admin.fields.locations.contact.l'),
        ph: t('admin.fields.locations.contact.ph'),
      },
      { k: 'desc', l: t('admin.fields.locations.desc.l'), m: 1, full: 1 },
    ];
  }
  return [
    { k: 'title', l: t('admin.fields.news.title.l'), full: 1 },
    {
      k: 'date',
      l: t('admin.fields.news.date.l'),
      ph: t('admin.fields.news.date.ph'),
    },
    { k: 'excerpt', l: t('admin.fields.news.excerpt.l'), m: 1, full: 1 },
    {
      k: 'status',
      l: t('admin.fields.news.status.l'),
      sel: ['draft', 'published'],
      slbl: t('admin.fields.news.status.options', { returnObjects: true }),
    },
  ];
}

const DEFS = {
  events: {
    id: '',
    title: '',
    date: '',
    time: '',
    location: '',
    registered: 0,
    active: true,
    type: 'Haupt-Event',
    desc: '',
  },
  locations: {
    id: '',
    name: '',
    initial: '',
    dept: '',
    time: '',
    room: '',
    contact: '',
    desc: '',
  },
  news: { id: '', title: '', date: '', excerpt: '', status: 'draft' },
};

function AdminCRUD({ type, items, onRefresh }) {
  const C = useT();
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...DEFS[type] });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fields = getFields(t, type);

  const labelMap = {
    events: t('admin.crud.labelEvents'),
    locations: t('admin.crud.labelLocations'),
    news: t('admin.crud.labelNews'),
  };

  const startNew = () => {
    setForm({ ...DEFS[type], id: uid() });
    setEditing(true);
  };
  const startEdit = (it) => {
    setForm({ ...it });
    setEditing(true);
  };
  const cancel = () => {
    setForm({ ...DEFS[type] });
    setEditing(false);
  };

  const save = async () => {
    if (!form[fields[0].k]?.toString().trim() || busy) return;
    setBusy(true);
    try {
      await upsertContent(type, form);
      await onRefresh();
      cancel();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await deleteContent(type, id);
      await onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, v) => {
    const it = items.find((i) => i.id === id);
    if (!it) return;
    setBusy(true);
    try {
      await upsertContent(type, { ...it, status: v });
      await onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <h2
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 800,
            color: C.dark,
            fontSize: '1.4rem',
            margin: 0,
          }}
        >
          {labelMap[type]} {t('admin.crud.manageSuffix')}
        </h2>
        <Btn v="gold" sm onClick={startNew}>
          <i className="fa-solid fa-plus" /> {t('admin.crud.newEntry')}
        </Btn>
      </div>

      {editing && (
        <div
          style={{
            background: '#fff',
            borderRadius: 18,
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 8px 30px rgba(0,0,0,.08)',
            border: '1px solid rgba(0,0,0,0.02)',
          }}
        >
          <h3
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              color: C.dark,
              marginBottom: '1.5rem',
              fontSize: '1.15rem',
              margin: 0,
            }}
          >
            {t('admin.crud.editEntry')}
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            {fields.map((f) => {
              if (f.type === 'cb') {
                return (
                  <label
                    key={f.k}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '.5rem',
                      fontSize: '.85rem',
                      color: C.dark,
                      cursor: 'pointer',
                      gridColumn: '1/-1',
                      margin: '0.5rem 0',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!form[f.k]}
                      onChange={(e) =>
                        setForm((x) => ({ ...x, [f.k]: e.target.checked }))
                      }
                      style={{ width: '16px', height: '16px' }}
                    />
                    {f.l}
                  </label>
                );
              }
              if (f.sel) {
                return (
                  <FieldSelect
                    key={f.k}
                    label={f.l}
                    value={form[f.k] || ''}
                    onChange={(e) =>
                      setForm((x) => ({ ...x, [f.k]: e.target.value }))
                    }
                    style={{ gridColumn: f.full ? '1/-1' : 'auto' }}
                  >
                    {f.sel.map((v, i) => (
                      <option key={v} value={v}>
                        {f.slbl[i]}
                      </option>
                    ))}
                  </FieldSelect>
                );
              }
              if (f.m) {
                return (
                  <FieldTA
                    key={f.k}
                    label={f.l}
                    value={form[f.k] || ''}
                    rows={3}
                    onChange={(e) =>
                      setForm((x) => ({ ...x, [f.k]: e.target.value }))
                    }
                    style={{ gridColumn: f.full ? '1/-1' : 'auto' }}
                  />
                );
              }
              return (
                <FieldInput
                  key={f.k}
                  label={f.l}
                  type={f.type || 'text'}
                  value={form[f.k] || ''}
                  placeholder={f.ph}
                  onChange={(e) =>
                    setForm((x) => ({
                      ...x,
                      [f.k]:
                        f.type === 'number'
                          ? Number(e.target.value)
                          : e.target.value,
                    }))
                  }
                  style={{ gridColumn: f.full ? '1/-1' : 'auto' }}
                />
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '.75rem', marginTop: '1.5rem' }}>
            <Btn v="gold" sm onClick={save} disabled={busy}>
              {t('admin.crud.save')}
            </Btn>
            <Btn v="muted" sm onClick={cancel} disabled={busy}>
              {t('admin.crud.cancel')}
            </Btn>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
        {items.map((it) => (
          <div
            key={it.id}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '1.25rem 1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              boxShadow: '0 2px 8px rgba(0,0,0,.03)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  color: C.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {it[fields[0].k]}
              </div>
              <div
                style={{
                  fontSize: '.76rem',
                  color: C.muted,
                  marginTop: '.25rem',
                }}
              >
                {type === 'events' && `📅 ${it.date} · 📍 ${it.location}`}
                {type === 'locations' && `⏰ ${it.time} · 🚪 ${it.room}`}
                {type === 'news' &&
                  `📅 ${it.date} · 📝 ${it.excerpt?.slice(0, 70)}…`}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '.5rem',
                flexShrink: 0,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {type === 'news' && (
                <>
                  <span
                    style={{
                      fontSize: '.7rem',
                      fontWeight: 700,
                      padding: '.25rem .75rem',
                      borderRadius: 20,
                      background:
                        it.status === 'published'
                          ? `${C.green}15`
                          : `${C.gold}15`,
                      color: it.status === 'published' ? C.green : C.goldH,
                    }}
                  >
                    {it.status === 'published'
                      ? t('admin.crud.active')
                      : t('admin.crud.draft')}
                  </span>
                  <Btn
                    v="muted"
                    sm
                    disabled={busy}
                    onClick={() =>
                      setStatus(
                        it.id,
                        it.status === 'published' ? 'draft' : 'published'
                      )
                    }
                  >
                    {it.status === 'published'
                      ? t('admin.crud.retract')
                      : t('admin.crud.publish')}
                  </Btn>
                </>
              )}
              {type === 'events' && (
                <span
                  style={{
                    fontSize: '.7rem',
                    fontWeight: 700,
                    padding: '.25rem .75rem',
                    borderRadius: 20,
                    background: it.active ? `${C.green}15` : `${C.red}15`,
                    color: it.active ? C.green : C.red,
                  }}
                >
                  {it.active ? t('admin.crud.visible') : t('admin.crud.hidden')}
                </span>
              )}
              <Btn v="muted" sm onClick={() => startEdit(it)}>
                <i className="fa-solid fa-pen" />
              </Btn>
              <Btn v="danger" sm disabled={busy} onClick={() => remove(it.id)}>
                <i className="fa-solid fa-trash-can" />
              </Btn>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p style={{ color: C.muted, padding: '2rem 0', textAlign: 'center' }}>
            {t('admin.crud.empty')}
          </p>
        )}
      </div>
    </div>
  );
}

/* ══ Prayer Moderation ══ */
function AdminPrayers({ prayers, onApprove, onDelete }) {
  const C = useT();
  const { t } = useTranslation();

  const pending = prayers.filter((p) => !p.approved);
  const approved = prayers.filter((p) => p.approved);

  return (
    <div>
      <h2
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 800,
          color: C.dark,
          fontSize: '1.4rem',
          marginBottom: '1.5rem',
          margin: 0,
        }}
      >
        {t('admin.prayers.title')}
      </h2>

      <div style={{ marginBottom: '3rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '.75rem',
            marginBottom: '1rem',
          }}
        >
          <h3
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              fontSize: '1.15rem',
              color: C.dark,
              margin: 0,
            }}
          >
            {t('admin.prayers.pendingTitle')}
          </h3>
          {pending.length > 0 && (
            <span
              style={{
                background: C.red,
                color: '#fff',
                borderRadius: 20,
                padding: '.2rem .7rem',
                fontSize: '.72rem',
                fontWeight: 700,
              }}
            >
              {pending.length}
            </span>
          )}
        </div>

        {pending.length === 0 ? (
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '1.5rem',
              textAlign: 'center',
              border: '1px solid rgba(0,0,0,0.02)',
              color: C.muted,
            }}
          >
            <i
              className="fa-solid fa-circle-check"
              style={{
                color: C.green,
                fontSize: '1.5rem',
                marginBottom: '0.5rem',
                display: 'block',
              }}
            />
            {t('admin.prayers.pendingEmpty')}
          </div>
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}
          >
            {pending.map((p) => (
              <div
                key={p.id}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  padding: '1.5rem',
                  borderLeft: `5px solid ${C.gold}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,.04)',
                }}
              >
                <div
                  style={{
                    fontSize: '.72rem',
                    color: C.muted,
                    fontWeight: 600,
                    marginBottom: '.5rem',
                  }}
                >
                  {t(`prayerWall.categories.${p.cat}`)} · {t('admin.prayers.creator')}: {p.author}
                </div>
                <p
                  style={{
                    color: C.text,
                    lineHeight: 1.6,
                    marginBottom: '1rem',
                    fontStyle: 'italic',
                    margin: 0,
                  }}
                >
                  „{p.text}“
                </p>
                <div style={{ display: 'flex', gap: '.5rem' }}>
                  <Btn v="success" sm onClick={() => onApprove(p.id)}>
                    <i className="fa-solid fa-check" /> {t('admin.prayers.approve')}
                  </Btn>
                  <Btn v="danger" sm onClick={() => onDelete(p.id)}>
                    <i className="fa-solid fa-trash" /> {t('admin.prayers.delete')}
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h3
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 700,
          fontSize: '1.15rem',
          color: C.dark,
          marginBottom: '1rem',
          margin: 0,
        }}
      >
        {t('admin.prayers.approvedTitle')} ({approved.length})
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        {approved.map((p) => (
          <div
            key={p.id}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '1rem 1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              borderLeft: `5px solid ${C.green}`,
              boxShadow: '0 2px 8px rgba(0,0,0,.02)',
            }}
          >
            <p
              style={{
                fontStyle: 'italic',
                color: C.muted,
                fontSize: '.85rem',
                flex: 1,
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              „{p.text}“
            </p>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: '.75rem', color: C.muted }}>
                {p.author}
              </span>
              <Btn v="danger" sm onClick={() => onDelete(p.id)}>
                <i className="fa-solid fa-trash" />
              </Btn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══ Design Customization ══ */
function AdminDesign({
  themeIdx,
  onSelectTheme,
  showSpenden,
  onToggleShowSpenden,
  memberCount,
  onUpdateMemberCount,
}) {
  const C = useT();
  const { t } = useTranslation();
  const [memberInput, setMemberInput] = useState(memberCount);
  const [savingMembers, setSavingMembers] = useState(false);
  const [membersSaved, setMembersSaved] = useState(false);

  useEffect(() => {
    setMemberInput(memberCount);
  }, [memberCount]);

  const saveMemberCount = async () => {
    setSavingMembers(true);
    setMembersSaved(false);
    try {
      await onUpdateMemberCount(memberInput);
      setMembersSaved(true);
    } finally {
      setSavingMembers(false);
    }
  };

  return (
    <div>
      <h2
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 800,
          color: C.dark,
          fontSize: '1.4rem',
          marginBottom: '.5rem',
          margin: 0,
        }}
      >
        {t('admin.design.title')}
      </h2>
      <p
        style={{
          color: C.muted,
          marginBottom: '2.5rem',
          fontSize: '.9rem',
          marginTop: 0,
        }}
      >
        {t('admin.design.sub')}
      </p>

      <div
        style={{
          marginBottom: '1rem',
          fontSize: '.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '.14em',
          color: C.muted,
        }}
      >
        {t('admin.design.schemesTitle')}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
          gap: '1.25rem',
          marginBottom: '3rem',
        }}
      >
        {THEMES.map((t, i) => (
          <button
            key={t.id}
            onClick={() => onSelectTheme(i)}
            style={{
              background: '#fff',
              borderRadius: 16,
              border:
                themeIdx === i
                  ? `2.5px solid ${t.gold}`
                  : '1px solid rgba(0,0,0,.08)',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
              overflow: 'hidden',
              transform: themeIdx === i ? 'scale(1.03)' : 'scale(1)',
              boxShadow:
                themeIdx === i
                  ? `0 10px 30px rgba(0,0,0,.15)`
                  : '0 2px 8px rgba(0,0,0,.03)',
              transition: 'all .2s ease-in-out',
            }}
          >
            {/* Header snippet */}
            <div
              style={{
                height: 32,
                background: t.dark,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
                borderBottom: `3px solid ${t.gold}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: t.gold,
                    opacity: 0.9,
                  }}
                />
                <div
                  style={{
                    width: 34,
                    height: 4,
                    borderRadius: 2,
                    background: t.gold,
                    opacity: 0.4,
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 3 }}>
                {[14, 20, 14].map((w, j) => (
                  <div
                    key={j}
                    style={{
                      width: w,
                      height: 3,
                      borderRadius: 1.5,
                      background: 'rgba(255,255,255,.2)',
                    }}
                  />
                ))}
              </div>
            </div>
            {/* Swatches block */}
            <div style={{ padding: '12px 14px', background: '#fff' }}>
              <div
                style={{
                  display: 'flex',
                  gap: 5,
                  marginBottom: 8,
                  alignItems: 'center',
                }}
              >
                {[t.dark, t.gray, t.gold, t.blue, t.green].map((col, j) => (
                  <div
                    key={j}
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: col,
                      border: j === 1 ? '1px solid rgba(0,0,0,.1)' : 'none',
                    }}
                  />
                ))}
                {themeIdx === i && (
                  <i
                    className="fa-solid fa-circle-check"
                    style={{
                      marginLeft: 'auto',
                      color: t.gold,
                      fontSize: '1rem',
                    }}
                  />
                )}
              </div>
              <div
                style={{ fontWeight: 700, fontSize: '.85rem', color: '#111' }}
              >
                {t.name}
              </div>
              <div
                style={{
                  fontSize: '.7rem',
                  color: '#6B7280',
                  marginTop: 2,
                  lineHeight: 1.3,
                }}
              >
                {t.desc}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div
        style={{ borderTop: '1px solid rgba(0,0,0,.08)', paddingTop: '2.5rem' }}
      >
        <div
          style={{
            marginBottom: '1rem',
            fontSize: '.72rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.14em',
            color: C.muted,
          }}
        >
          {t('admin.design.settingsTitle')}
        </div>
        <div
          style={{
            background: '#fff',
            borderRadius: 18,
            padding: '2rem',
            maxWidth: 540,
            boxShadow: '0 4px 15px rgba(0,0,0,.03)',
            border: '1px solid rgba(0,0,0,0.02)',
          }}
        >
          <h3
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              color: C.dark,
              fontSize: '1.1rem',
              marginBottom: '1.25rem',
              margin: 0,
            }}
          >
            {t('admin.design.visibilityTitle')}
          </h3>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 0',
              borderBottom: `1px solid ${C.bg}`,
            }}
          >
            <div style={{ paddingRight: '1rem' }}>
              <div
                style={{ fontWeight: 700, fontSize: '.9rem', color: C.text }}
              >
                {t('admin.design.donationToggleTitle')}
              </div>
              <div
                style={{
                  fontSize: '.76rem',
                  color: C.muted,
                  marginTop: '.25rem',
                  lineHeight: 1.4,
                }}
              >
                {t('admin.design.donationToggleDesc')}
              </div>
            </div>
            <button
              onClick={onToggleShowSpenden}
              style={{
                width: 50,
                height: 26,
                borderRadius: 15,
                border: 'none',
                cursor: 'pointer',
                background: showSpenden ? C.gold : 'rgba(0,0,0,.18)',
                transition: 'background .2s',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 3,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#fff',
                  left: showSpenden ? 27 : 3,
                  transition: 'left .2s',
                  boxShadow: '0 1px 4px rgba(0,0,0,.25)',
                }}
              />
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyTarget: 'space-between',
              justifyContent: 'space-between',
              paddingTop: '1.25rem',
            }}
          >
            <div style={{ fontSize: '.82rem', color: C.muted }}>
              {t('admin.design.donationStatusLabel')}{' '}
              <strong style={{ color: C.dark }}>
                {showSpenden ? t('admin.design.visible') : t('admin.design.disabled')}
              </strong>
            </div>
            <span
              style={{
                fontSize: '.7rem',
                fontWeight: 700,
                padding: '.25rem .75rem',
                borderRadius: 20,
                background: showSpenden ? `${C.green}15` : `${C.gold}15`,
                color: showSpenden ? C.green : C.goldH,
              }}
            >
              {showSpenden ? t('admin.design.active') : t('admin.design.inactive')}
            </span>
          </div>
        </div>

        <div
          style={{
            background: '#fff',
            borderRadius: 18,
            padding: '2rem',
            maxWidth: 540,
            marginTop: '1.75rem',
            boxShadow: '0 4px 15px rgba(0,0,0,.03)',
            border: '1px solid rgba(0,0,0,0.02)',
          }}
        >
          <h3
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              color: C.dark,
              fontSize: '1.1rem',
              marginBottom: '.5rem',
              margin: 0,
            }}
          >
            {t('admin.design.memberCountTitle')}
          </h3>
          <p
            style={{
              fontSize: '.8rem',
              color: C.muted,
              lineHeight: 1.5,
              marginTop: '.4rem',
              marginBottom: '1.25rem',
            }}
          >
            {t('admin.design.memberCountDesc')}
          </p>
          <div
            style={{
              display: 'flex',
              gap: '.75rem',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 160px' }}>
              <FieldInput
                label={t('admin.design.memberCountLabel')}
                type="number"
                min="0"
                value={memberInput}
                onChange={(e) => {
                  setMemberInput(e.target.value);
                  setMembersSaved(false);
                }}
              />
            </div>
            <Btn
              v="gold"
              sm
              disabled={savingMembers}
              onClick={saveMemberCount}
              sx={{ marginTop: '1.85rem' }}
            >
              {savingMembers ? t('admin.design.memberCountSaving') : t('admin.design.memberCountSave')}
            </Btn>
          </div>
          {membersSaved && (
            <p
              style={{
                color: C.green,
                fontSize: '.78rem',
                fontWeight: 600,
                marginTop: '.75rem',
                marginBottom: 0,
              }}
            >
              <i className="fa-solid fa-circle-check" /> {t('admin.design.memberCountSaved')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══ Static Pages ══ */
function UeberUns() {
  const C = useT();
  const { t } = useTranslation();

  const roles = t('ueberUns.roles', { returnObjects: true });

  return (
    <div
      style={{ background: C.bg, minHeight: '80vh', padding: '5rem 1.5rem' }}
    >
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <SectionHeader
          eyebrow={t('ueberUns.eyebrow')}
          title={t('ueberUns.title')}
        />

        <div
          style={{
            background: `linear-gradient(135deg, ${C.gray}, ${C.dark}f5)`,
            border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 24,
            padding: '2.25rem',
            marginBottom: '1.5rem',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,.35)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: C.gold,
            }}
          />
          <i
            className="fa-solid fa-quote-left"
            style={{
              fontSize: '2rem',
              color: C.gold,
              opacity: 0.35,
              marginBottom: '1rem',
              display: 'block',
            }}
          />
          <blockquote
            style={{
              fontSize: '1.05rem',
              lineHeight: 1.8,
              color: 'rgba(255,255,255,.9)',
              fontStyle: 'italic',
              margin: '0 0 1.25rem',
              padding: 0,
            }}
          >
            {t('ueberUns.verseText')}
          </blockquote>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '.5rem',
              paddingTop: '1.25rem',
              borderTop: '1px solid rgba(255,255,255,.08)',
            }}
          >
            <span
              style={{ fontWeight: 700, color: C.gold, fontSize: '.9rem' }}
            >
              {t('ueberUns.verseRef')}
            </span>
            <span
              style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.45)' }}
            >
              {t('ueberUns.verseTranslation')}
            </span>
          </div>
        </div>

        <div
          style={{
            background: C.white,
            borderRadius: 24,
            padding: '2.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(0,0,0,.06)',
            boxShadow: '0 4px 15px rgba(0,0,0,.02)',
          }}
        >
          <h2
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              color: C.dark,
              marginBottom: '1rem',
              fontSize: '1.4rem',
              margin: 0,
            }}
          >
            {t('ueberUns.card1Title')}
          </h2>
          <p
            style={{
              color: C.muted,
              lineHeight: 1.85,
              fontSize: '0.95rem',
              marginBottom: '1rem',
              marginTop: '.75rem',
            }}
          >
            {t('ueberUns.card1Text1')}
          </p>
          <p
            style={{
              color: C.muted,
              lineHeight: 1.85,
              fontSize: '0.95rem',
              margin: 0,
            }}
          >
            {t('ueberUns.card1Text2')}
          </p>
        </div>

        <div
          style={{
            background: C.dark,
            borderRadius: 24,
            padding: '2.5rem',
            marginBottom: '1.5rem',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: '-3rem',
              right: '-3rem',
              width: 150,
              height: 150,
              background: C.gold,
              borderRadius: '50%',
              filter: 'blur(60px)',
              opacity: 0.1,
            }}
          />
          <h2
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              fontSize: '1.3rem',
              marginBottom: '1rem',
              margin: 0,
            }}
          >
            {t('ueberUns.card2Title')}
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,.75)',
              lineHeight: 1.85,
              fontSize: '0.95rem',
              margin: '0.5rem 0 0',
            }}
          >
            {t('ueberUns.card2Text')}
          </p>
        </div>

        <div
          style={{
            background: C.white,
            borderRadius: 24,
            padding: '2.5rem',
            border: '1px solid rgba(0,0,0,.06)',
            boxShadow: '0 4px 15px rgba(0,0,0,.02)',
          }}
        >
          <h2
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              color: C.dark,
              marginBottom: '1.5rem',
              fontSize: '1.3rem',
              margin: 0,
            }}
          >
            {t('ueberUns.card3Title')}
          </h2>
          {roles.map(([r, d], i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1rem 0',
                borderBottom: i < roles.length - 1 ? '1px solid rgba(0,0,0,.05)' : 'none',
                flexWrap: 'wrap',
                gap: '.5rem',
              }}
            >
              <span
                style={{ fontWeight: 700, color: C.text, fontSize: '.9rem' }}
              >
                {r}
              </span>
              <span style={{ fontSize: '.85rem', color: C.muted }}>{d}</span>
            </div>
          ))}
          <p
            style={{
              marginTop: '1.5rem',
              fontSize: '.76rem',
              color: C.muted,
              margin: 0,
            }}
          >
            {t('ueberUns.footnote')}
          </p>
        </div>
      </div>
    </div>
  );
}

function FAQ() {
  const C = useT();
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);

  const items = t('faq.items', { returnObjects: true });

  return (
    <div
      style={{ background: C.bg, minHeight: '80vh', padding: '5rem 1.5rem' }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <SectionHeader eyebrow={t('faq.eyebrow')} title={t('faq.title')} />

        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}
        >
          {items.map(([q, a], i) => (
            <div
              key={i}
              style={{
                background: C.white,
                borderRadius: 16,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,.06)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
              }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1.25rem 1.5rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 700,
                  fontSize: '.95rem',
                  color: C.dark,
                  textAlign: 'left',
                  gap: '1rem',
                }}
              >
                {q}
                <i
                  className={`fa-solid fa-chevron-down`}
                  style={{
                    transform: open === i ? 'rotate(180deg)' : 'none',
                    transition: 'transform .25s',
                    color: C.gold,
                    fontSize: '.9rem',
                  }}
                />
              </button>
              {open === i && (
                <div
                  style={{
                    padding: '0 1.5rem 1.5rem',
                    color: C.muted,
                    fontSize: '.9rem',
                    lineHeight: 1.8,
                  }}
                >
                  {a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Impressum() {
  const C = useT();
  const { t } = useTranslation();

  return (
    <div
      style={{ background: C.bg, minHeight: '70vh', padding: '5rem 1.5rem' }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <SectionHeader eyebrow={t('impressum.eyebrow')} title={t('impressum.title')} />

        <div
          style={{
            background: C.white,
            borderRadius: 24,
            padding: '2.5rem',
            border: '1px solid rgba(0,0,0,.06)',
            boxShadow: '0 4px 15px rgba(0,0,0,.02)',
          }}
        >
          <p
            style={{
              color: C.muted,
              lineHeight: 1.85,
              fontSize: '0.92rem',
              marginBottom: '1.5rem',
              marginTop: 0,
            }}
          >
            <strong style={{ color: C.dark, fontSize: '1rem' }}>
              {t('impressum.operatorTitle')}
            </strong>
            <br />
            {t('impressum.operatorLine1')}
            <br />
            {t('impressum.operatorLine2')}
            <br />
            {t('impressum.emailLabel')}{' '}
            <span style={{ color: C.dark, fontWeight: 600 }}>
              christians.at.karcher@gmail.com
            </span>
          </p>

          <div
            style={{
              background: C.bg,
              borderRadius: 14,
              padding: '1.25rem',
              fontSize: '.85rem',
              color: C.muted,
              lineHeight: 1.8,
              marginBottom: '1.5rem',
            }}
          >
            <strong style={{ color: C.dark }}>
              {t('impressum.trademarkTitle')}
            </strong>
            <br />
            {t('impressum.trademarkText')}
          </div>

          <p
            style={{
              color: C.muted,
              fontSize: '.85rem',
              lineHeight: 1.8,
              margin: 0,
            }}
          >
            <strong style={{ color: C.dark }}>
              {t('impressum.gdprTitle')}
            </strong>
            <br />
            {t('impressum.gdprText')}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ══ App Root ══ */
export default function App() {
  const { t } = useTranslation();
  const [page, setPage] = useState('home');
  const [auth, setAuth] = useState({
    isLoggedIn: false,
    user: null,
    id: null,
    role: null,
  });
  const [showAuth, setShowAuth] = useState(false);

  const [data, setData] = useState({ locations: [], events: [], news: [] });
  const [prayers, setPrayers] = useState([]);
  const [likedIds, setLikedIds] = useState({});
  const [settings, setSettings] = useState({
    memberCount: 0,
    showDonations: true,
    themeIdx: 0,
  });

  const C = THEMES[settings.themeIdx] || THEMES[0];

  const refreshContent = async () => {
    try {
      const [locations, events, news] = await Promise.all([
        listContent('locations'),
        listContent('events'),
        listContent('news'),
      ]);
      setData({ locations, events, news });
    } catch (e) {
      console.error('Failed to load content from Supabase:', e);
    }
  };

  const refreshPrayers = async () => {
    try {
      const list = await listAllPrayers();
      setPrayers(list);
    } catch (e) {
      console.error('Failed to load prayers from Supabase:', e);
    }
  };

  const refreshSettings = async () => {
    try {
      const s = await getSettings();
      setSettings(s);
    } catch (e) {
      console.error('Failed to load settings from Supabase:', e);
    }
  };

  const refreshLikes = async (userId) => {
    if (!userId) {
      setLikedIds({});
      return;
    }
    try {
      const ids = await listMyPrayerLikes(userId);
      setLikedIds(Object.fromEntries(ids.map((id) => [id, true])));
    } catch (e) {
      console.error('Failed to load prayer likes from Supabase:', e);
    }
  };

  // Resolve the Supabase auth session (and keep it in sync)
  useEffect(() => {
    const applySession = async (session) => {
      if (session?.user) {
        try {
          const profile = await fetchProfile(session.user.id);
          setAuth({
            isLoggedIn: true,
            user: profile?.email || session.user.email,
            id: session.user.id,
            role: profile?.role || 'user',
          });
        } catch (e) {
          setAuth({
            isLoggedIn: true,
            user: session.user.email,
            id: session.user.id,
            role: 'user',
          });
        }
      } else {
        setAuth({ isLoggedIn: false, user: null, id: null, role: null });
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load public content once on mount
  useEffect(() => {
    refreshContent();
    refreshSettings();
  }, []);

  // Reload prayers / likes whenever the auth state changes (RLS decides
  // exactly which rows each user is allowed to see)
  useEffect(() => {
    refreshPrayers();
    refreshLikes(auth.id);
    // Intentionally scoped to auth changes only; refreshPrayers/refreshLikes
    // are stable-enough helpers and re-running on every render is undesired.
  }, [auth.id, auth.isLoggedIn]); // eslint-disable-line

  // Loading fonts and styles dynamically
  useEffect(() => {
    const linkFont = document.createElement('link');
    linkFont.href =
      'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap';
    linkFont.rel = 'stylesheet';
    document.head.appendChild(linkFont);

    const linkFA = document.createElement('link');
    linkFA.href =
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    linkFA.rel = 'stylesheet';
    document.head.appendChild(linkFA);

    document.body.style.margin = '0';
    document.body.style.fontFamily = "'Inter', system-ui, sans-serif";
    document.body.style.background = C.bg;
  }, [C.bg]);

  const navigateTo = (p) => {
    setPage(p);
    window.scrollTo(0, 0);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error('Sign out failed:', e);
    }
    navigateTo('home');
  };

  const handleSubmitPrayer = async ({ text, author, cat }) => {
    await submitPrayer({ id: uid(), text, author, cat, userId: auth.id });
    await refreshPrayers();
  };

  const handleToggleLike = async (prayerId) => {
    await togglePrayerLike(prayerId);
    await Promise.all([refreshPrayers(), refreshLikes(auth.id)]);
  };

  const handleApprovePrayer = async (id) => {
    await approvePrayer(id);
    await refreshPrayers();
  };

  const handleDeletePrayer = async (id) => {
    await deletePrayer(id);
    await refreshPrayers();
  };

  const handleUpdateMemberCount = async (memberCount) => {
    const s = await updateSettings({ memberCount });
    setSettings(s);
  };

  const handleToggleShowDonations = async () => {
    const s = await updateSettings({ showDonations: !settings.showDonations });
    setSettings(s);
  };

  const handleUpdateThemeIdx = async (themeIdx) => {
    const s = await updateSettings({ themeIdx });
    setSettings(s);
  };

  return (
    <Ctx.Provider value={C}>
      <div
        style={{
          minHeight: '100vh',
          background: C.bg,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <LegalBar />
          <Nav
            page={page}
            go={navigateTo}
            auth={auth}
            onAuth={() => setShowAuth(true)}
            onLogout={handleLogout}
          />

          {page === 'home' && (
            <>
              <Hero
                go={navigateTo}
                data={data}
                memberCount={settings.memberCount}
                onAuth={() => setShowAuth(true)}
              />
              <AboutSection />
              <LocationFinder data={data} />
              <Pillars />
              <PrayerWall
                auth={auth}
                prayers={prayers}
                likedIds={likedIds}
                onSubmit={handleSubmitPrayer}
                onToggleLike={handleToggleLike}
                onAuth={() => setShowAuth(true)}
              />
              <EventsSection data={data} />
              <ContactSection />
            </>
          )}

          {page === 'gebete' && (
            <PrayerWall
              auth={auth}
              prayers={prayers}
              likedIds={likedIds}
              onSubmit={handleSubmitPrayer}
              onToggleLike={handleToggleLike}
              onAuth={() => setShowAuth(true)}
            />
          )}
          {page === 'ueber-uns' && <UeberUns />}
          {page === 'faq' && <FAQ />}
          {page === 'impressum' && <Impressum />}

          {page === 'admin' && auth.role === 'admin' && (
            <AdminDashboard
              data={data}
              refreshContent={refreshContent}
              prayers={prayers}
              onApprovePrayer={handleApprovePrayer}
              onDeletePrayer={handleDeletePrayer}
              themeIdx={settings.themeIdx}
              onSelectTheme={handleUpdateThemeIdx}
              showSpenden={settings.showDonations}
              onToggleShowSpenden={handleToggleShowDonations}
              memberCount={settings.memberCount}
              onUpdateMemberCount={handleUpdateMemberCount}
            />
          )}

          {page === 'admin' && auth.role !== 'admin' && (
            <div
              style={{
                padding: '8rem 2rem',
                textAlign: 'center',
                color: C.muted,
              }}
            >
              <i
                className="fa-solid fa-lock"
                style={{ fontSize: '3rem', color: C.red, marginBottom: '1rem' }}
              />
              <h3 style={{ margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                {t('admin.accessDeniedTitle')}
              </h3>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {t('admin.accessDeniedText')}
              </p>
            </div>
          )}
        </div>

        {page !== 'admin' && (
          <Footer go={navigateTo} showSpenden={settings.showDonations} />
        )}

        {showAuth && (
          <AuthModal
            onClose={() => setShowAuth(false)}
            onSuccess={() => setShowAuth(false)}
          />
        )}
      </div>
    </Ctx.Provider>
  );
}
